import { useCallback, useMemo, useState } from 'react';
import { notification } from 'antd';
import { useDownloadQuoteGate } from '@/features/billing/hooks/useDownloadQuoteGate';
import { submitConfigureDeployment } from './submitConfigureDeployment.js';
import {
  filterEligibleTrainings,
  findTrainingForUsecaseVersion,
} from './eligibleTrainings.js';
import { buildTaskKey } from './taskKeys';

/** ``download/quote`` items for the configure modal's selected tasks. */
export function quoteItemsFromTasks(tasks) {
  return (tasks || []).map(({ training }) => ({
    llm_usecase_id: training.llm_usecase_id,
    version: training.version,
    label: `${training.usecase_name ?? training.llm_usecase_id} v${training.version}`,
  }));
}

const CLOSED_CONFIGURE_MODAL = Object.freeze({
  open: false,
  mode: null,
  formatMode: 'all',
  training: null,
  instancesCapErrorByTaskKey: {},
  focusedTaskKey: null,
});

/**
 * Callers like the task-page drawer only carry a sparse version object, so
 * merge in the full trainings row (trained prediction strategy, long-text
 * flag, …) when we have it — that's what seeds the configure modal's runtime
 * defaults on the Models page. Returns ``training`` itself when no row matches.
 */
function withFullTrainingRow(trainings, training) {
  const fullRow = findTrainingForUsecaseVersion(
    trainings,
    training.llm_usecase_id,
    training.version
  );
  return fullRow ? { ...fullRow, ...training } : training;
}

/**
 * Shared "Download model" flow used by every surface that lets the user
 * download / push a trained model (Models page rows, task-page kebab, Model
 * Details drawer on the task page, …). Pair with
 * ``ConfigureDeploymentFlowModals`` so the modal chain is not copied.
 *
 * Owns the modal-chain state (single Download Model modal — format + infra +
 * runtime knobs — → download refusal → duplicate-registry confirm) and the
 * submit path that reports progress through ``setProgressStatus`` so the
 * global FloatingProgressBar reflects it. Download-mode submits are quoted
 * first (``downloadGate``), so a paid download is confirmed — or refused for
 * lack of credits — before any ``download-model-request`` is sent; deploys
 * are never quoted.
 *
 * @param {object} params
 * @param {import('react').Dispatch<unknown>} params.setProgressStatus
 * @param {(training: object) => void} [params.onDeploySuccess] — optimistic UI
 *   update / refetch after a deploy request is accepted.
 * @param {object[]|null} [params.trainings] — enriched trainings list when the
 *   caller already has one (Models page). Feeds the configure modal's Add-Task
 *   picker and version selects.
 * @param {() => Promise<object[]>} [params.loadTrainings] — lazy loader used
 *   when the caller has no trainings list (task page drawer). Invoked once,
 *   when the download flow first opens.
 */
export function useConfigureDeploymentFlow({
  setProgressStatus,
  onDeploySuccess,
  trainings = null,
  loadTrainings = null,
}) {
  // Download Model modal state — shared by local downloads (raw files /
  // Docker image), registry pushes and live deploys. ``formatMode`` selects
  // which format cards the modal offers: 'all' for the Download entry point,
  // 'push' for Push-model. The chosen artifact type lives in the modal and
  // arrives with its confirm payload.
  const [configureModal, setConfigureModal] = useState(CLOSED_CONFIGURE_MODAL);

  const [duplicatePushConfirm, setDuplicatePushConfirm] = useState(null);
  const [duplicatePushSubmitting, setDuplicatePushSubmitting] = useState(false);

  const [fetchedTrainings, setFetchedTrainings] = useState(null);

  const resolvedTrainings = useMemo(
    () => trainings ?? fetchedTrainings ?? [],
    [trainings, fetchedTrainings]
  );

  const ensureTrainingsLoaded = useCallback(() => {
    if (trainings) return Promise.resolve(trainings);
    if (fetchedTrainings) return Promise.resolve(fetchedTrainings);
    if (!loadTrainings) return Promise.resolve([]);
    return loadTrainings()
      .then((list) => {
        const rows = Array.isArray(list) ? list : [];
        setFetchedTrainings(rows);
        return rows;
      })
      .catch(() => {
        setFetchedTrainings([]);
        return [];
      });
  }, [trainings, fetchedTrainings, loadTrainings]);

  const openDownloadModal = useCallback(
    (training, formatMode) => {
      if (!training) return;
      const seeded = withFullTrainingRow(resolvedTrainings, training);
      setConfigureModal({
        ...CLOSED_CONFIGURE_MODAL,
        open: true,
        mode: 'download',
        formatMode,
        training: seeded,
      });
      if (seeded !== training) return;
      // Sparse row and no trainings list yet (task page): enrich the open
      // modal once the lazy loader resolves, as long as it still shows the
      // same model version.
      ensureTrainingsLoaded().then((rows) => {
        const enriched = withFullTrainingRow(rows, training);
        if (enriched === training) return;
        setConfigureModal((prev) =>
          prev.open &&
          prev.training &&
          buildTaskKey(prev.training) === buildTaskKey(training)
            ? { ...prev, training: enriched }
            : prev
        );
      });
    },
    [resolvedTrainings, ensureTrainingsLoaded]
  );

  const handleDownloadTraining = useCallback(
    (training) => openDownloadModal(training, 'all'),
    [openDownloadModal]
  );

  const handlePushTraining = useCallback(
    (training) => openDownloadModal(training, 'push'),
    [openDownloadModal]
  );

  const closeConfigureModal = useCallback(() => {
    setConfigureModal(CLOSED_CONFIGURE_MODAL);
  }, []);

  const handleConfigureCancel = useCallback(() => {
    closeConfigureModal();
  }, [closeConfigureModal]);

  const downloadGate = useDownloadQuoteGate();
  const { requestDownload, showInsufficientFromLastQuote } = downloadGate;

  const runConfigureSubmit = useCallback(
    async ({ mode, sharedConfig, tasks, artifactType, forcePush }) => {
      try {
        await submitConfigureDeployment({
          mode,
          sharedConfig,
          tasks,
          artifactType,
          setProgressStatus,
          onDeploySuccess,
          forcePush,
          // Quoted already — a 402 is surfaced by the gate's modal below.
          suppressFreeUsageGate: mode === 'download',
        });
        closeConfigureModal();
      } catch (err) {
        // The quote said the download was affordable but the balance moved
        // before the request landed: show "Not enough credits" from that quote.
        if (err?.status === 402 && mode === 'download') {
          showInsufficientFromLastQuote();
          return;
        }
        if (err?.isInstancesCapError && err.taskKey) {
          setConfigureModal((prev) => ({
            ...prev,
            instancesCapErrorByTaskKey: {
              ...prev.instancesCapErrorByTaskKey,
              [err.taskKey]: err.message,
            },
            focusedTaskKey: err.taskKey,
          }));
          return;
        }
        // Image already pushed before: ask the operator to confirm an
        // overriding push instead of silently failing.
        if (err?.isDuplicatePush) {
          setDuplicatePushConfirm({
            mode,
            sharedConfig,
            tasks,
            artifactType,
            previousPush: err.previousPush || null,
          });
          return;
        }
        notification.error({
          message: mode === 'download' ? 'Download Failed' : 'Deployment Failed',
          description: err?.message || 'Request failed',
          duration: 6,
        });
      }
    },
    [setProgressStatus, onDeploySuccess, closeConfigureModal, showInsufficientFromLastQuote]
  );

  const handleConfigureConfirm = useCallback(
    async ({ mode, sharedConfig, tasks, artifactType }) => {
      if (!tasks?.length) {
        closeConfigureModal();
        return;
      }
      const submit = () =>
        runConfigureSubmit({ mode, sharedConfig, tasks, artifactType, forcePush: false });
      if (mode !== 'download') {
        await submit();
        return;
      }
      // Quote first. The configure modal's footer already showed this price
      // (``useDownloadQuote``), so the Download click is the consent and only
      // a refusal for lack of credits still opens a modal.
      await requestDownload({ items: quoteItemsFromTasks(tasks), onApproved: submit, confirmInline: true });
    },
    [runConfigureSubmit, closeConfigureModal, requestDownload]
  );

  const handleDuplicatePushCancel = useCallback(() => {
    if (duplicatePushSubmitting) return;
    setDuplicatePushConfirm(null);
  }, [duplicatePushSubmitting]);

  const handleDuplicatePushConfirm = useCallback(async () => {
    if (!duplicatePushConfirm || duplicatePushSubmitting) return;
    const { mode, sharedConfig, tasks, artifactType } = duplicatePushConfirm;
    setDuplicatePushSubmitting(true);
    try {
      setDuplicatePushConfirm(null);
      await runConfigureSubmit({ mode, sharedConfig, tasks, artifactType, forcePush: true });
    } finally {
      setDuplicatePushSubmitting(false);
    }
  }, [duplicatePushConfirm, duplicatePushSubmitting, runConfigureSubmit]);

  const handleClearInstancesCapErrorForTask = useCallback((taskKey) => {
    setConfigureModal((prev) => {
      if (!prev.instancesCapErrorByTaskKey[taskKey]) return prev;
      const next = { ...prev.instancesCapErrorByTaskKey };
      delete next[taskKey];
      return { ...prev, instancesCapErrorByTaskKey: next };
    });
  }, []);

  const handleFocusedTaskKeyChange = useCallback((taskKey) => {
    setConfigureModal((prev) => ({ ...prev, focusedTaskKey: taskKey }));
  }, []);

  const eligibleTrainings = useMemo(
    () => filterEligibleTrainings(resolvedTrainings, configureModal.mode, []),
    [resolvedTrainings, configureModal.mode]
  );

  return {
    // Download Model modal (format + configuration in one step)
    handleDownloadTraining,
    handlePushTraining,
    configureModal,
    eligibleTrainings,
    handleConfigureCancel,
    handleConfigureConfirm,
    handleClearInstancesCapErrorForTask,
    handleFocusedTaskKeyChange,
    // Duplicate-registry-push confirm modal
    duplicatePushConfirm,
    duplicatePushSubmitting,
    handleDuplicatePushCancel,
    handleDuplicatePushConfirm,
    // Download quote: not-enough-credits modal (the price itself is in the configure modal footer)
    downloadGate,
  };
}
